(() => {
  'use strict';

  const dialog = document.querySelector('dialog[data-br-drawer]');
  if (!dialog || typeof dialog.showModal !== 'function' || !window.fetch) return;
  const sectionId = dialog.dataset.sectionId || 'br-cart-drawer';
  const priorityVariant = dialog.dataset.priorityVariant;
  const priorityHandle = 'beam-rivals-skip-the-line-holiday-priority';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartURL = `${root.replace(/\/?$/, '/')}cart`;
  let busy = false;
  let opener = null;
  let closeTimer;
  let disabledControls = [];
  let stateUnavailable = false;
  let pendingUpgrade = null;

  const content = () => dialog.querySelector('[data-br-cart-content]');
  const closeButton = () => dialog.querySelector('[data-br-cart-close]');

  function setError(message, form) {
    const targets = [dialog.querySelector('[data-br-cart-error]'), form && form.querySelector('[data-cart-error]')];
    targets.forEach(target => { if (target) target.textContent = message; });
  }

  function updateCount(count) {
    const number = Number(count);
    if (!Number.isInteger(number) || number < 0) return;
    document.querySelectorAll('[data-br-cart-count]').forEach(badge => { badge.textContent = String(number); });
    document.querySelectorAll('[data-br-cart-open]').forEach(link => {
      link.setAttribute('aria-label', `Shopping bag, ${number} items`);
    });
  }

  function lockControls() {
    const forms = Array.from(document.querySelectorAll('form[data-br-add], form[data-br-extra], form[data-br-upgrade], form[data-br-cart-page]'));
    const controls = new Set(dialog.querySelectorAll('button:not([data-br-cart-close]), input, select'));
    forms.forEach(form => Array.from(form.elements).forEach(control => controls.add(control)));
    controls.forEach(control => {
      if (!('disabled' in control) || disabledControls.some(entry => entry.control === control)) return;
      disabledControls.push({ control, disabled: control.disabled });
      control.disabled = true;
    });
  }

  function setBusy(value) {
    busy = value;
    dialog.classList.toggle('is-loading', value);
    dialog.setAttribute('aria-busy', String(value));
    const status = dialog.querySelector('[data-br-cart-loading]');
    if (status) {
      status.hidden = !value;
      status.textContent = value ? 'Updating your bag…' : '';
    }
    if (value) lockControls();
    else {
      disabledControls.forEach(({ control, disabled }) => { if (control.isConnected) control.disabled = disabled; });
      disabledControls = [];
    }
  }

  function openDrawer(trigger) {
    clearTimeout(closeTimer);
    dialog.classList.remove('is-closing');
    if (!dialog.open) {
      opener = trigger && !dialog.contains(trigger) ? trigger : document.activeElement;
      dialog.showModal();
      document.body.classList.add('br-cart-open');
      if (closeButton()) closeButton().focus({ preventScroll: true });
    }
  }

  function closeDrawer() {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.close();
      dialog.classList.remove('is-closing');
    };
    if (reducedMotion.matches) finish();
    else {
      dialog.classList.add('is-closing');
      closeTimer = setTimeout(finish, 250);
    }
  }

  function focusDescriptor(element) {
    if (!element || !dialog.contains(element)) return null;
    return {
      key: element.dataset.key,
      label: element.getAttribute('aria-label'),
      text: element.textContent,
      element
    };
  }

  function restoreDrawerFocus(previous) {
    if (!dialog.open || !previous) return;
    if (previous.element.isConnected && !previous.element.disabled) {
      previous.element.focus({ preventScroll: true });
      return;
    }
    const replacement = Array.from(dialog.querySelectorAll('[data-br-quantity]')).find(button =>
      button.dataset.key === previous.key &&
      (previous.label ? button.getAttribute('aria-label') === previous.label : button.textContent === previous.text));
    const target = replacement || closeButton();
    if (target && !target.disabled) target.focus({ preventScroll: true });
  }

  function renderSection(html) {
    if (typeof html !== 'string' || !html.trim()) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-br-cart-content]');
    const existing = content();
    if (!fresh || !existing || !/^\d+$/.test(fresh.dataset.itemCount || '')) return false;
    // Only Shopify's rendered cart region changes; the open dialog and its focus trap stay mounted.
    existing.replaceWith(document.importNode(fresh, true));
    updateCount(fresh.dataset.itemCount);
    stateUnavailable = Boolean(pendingUpgrade);
    if (busy) lockControls();
    return true;
  }

  async function requestJSON(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...options.headers },
        signal: controller.signal
      });
      let data;
      try { data = await response.json(); } catch (_) { throw new Error('Your bag could not be confirmed. Open your bag before trying again.'); }
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'This item could not be updated. Please check your bag and try again.');
        error.cartResponse = data;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }

  async function syncPriority(cart) {
    const hasSet = cart.items.some(item => item.handle === 'beam-rivals-neon-battle-set' && item.quantity > 0);
    const updates = {};
    // Order-level services are optional and charged once, even after adding another set.
    for (const handle of [priorityHandle, 'beam-rivals-shipping-protection']) {
      const lines = cart.items.filter(item => item.handle === handle || (handle === priorityHandle && priorityVariant && String(item.variant_id) === priorityVariant));
      lines.forEach((item, index) => {
        const quantity = hasSet && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
    }
    if (Object.keys(updates).length) {
      cart = await requestJSON(`${cartURL}/update.js`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates})});
    }
    return cart;
  }

  async function refreshSection() {
    if (pendingUpgrade) await finishUpgradeProperties(await requestJSON(`${cartURL}.js`, { cache: 'no-store' }));
    const currentCart = await syncPriority(await requestJSON(`${cartURL}.js`, {cache:'no-store'}));
    if(window.BeamBands) await window.BeamBands.sync(currentCart,requestJSON,cartURL);
    const url = new URL(cartURL, location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionId);
    url.searchParams.set('_br', String(Date.now()));
    const sections = await requestJSON(url.href, { cache: 'no-store', headers: {Accept:'text/html'} });
    if (!renderSection(sections[sectionId])) throw new Error('Your bag was updated, but its preview could not load. Open your bag to review it.');
  }

  async function reconcile(response) {
    if (pendingUpgrade) {
      const cart = response && Array.isArray(response.items) ? response : await requestJSON(`${cartURL}.js`, { cache: 'no-store' });
      response = await finishUpgradeProperties(cart);
    }
    // Always reconcile optional priority and free gifts before rendering authoritative totals.
    // A missing section does not mean an add failed. Fetch display state, never replay the mutation.
    await refreshSection();
  }

  async function refreshCountOnly() {
    try { updateCount((await requestJSON(`${cartURL}.js`, { cache: 'no-store' })).item_count); } catch (_) { /* Keep the last confirmed count. */ }
  }

  async function finishUpgradeProperties(cart) {
    if (!pendingUpgrade) return cart;
    const upgrade = pendingUpgrade;
    const lines = cart.items.filter(item => String(item.variant_id || item.id) === upgrade.variantId);
    // A rejected update, or an item removed in another tab, needs no bonus metadata change.
    if (lines.length === 0) { pendingUpgrade = null; return cart; }
    if (lines.length !== 1) throw new Error('Please open your full bag to review this upgrade.');
    const line = lines[0];
    const matches = item => item && Object.entries(upgrade.properties).every(([key, value]) => item.properties && item.properties[key] === value);
    if (matches(line)) { pendingUpgrade = null; return cart; }
    const body = new FormData();
    body.set('id', line.key);
    body.set('quantity', String(line.quantity));
    Object.entries({ ...line.properties, ...upgrade.properties }).forEach(([key, value]) => body.set(`properties[${key}]`, value));
    body.set('sections', sectionId);
    body.set('sections_url', location.pathname);
    // update.js cannot set a replacement variant's line properties. This second change is
    // idempotent metadata only; retries never add another pack or repeat the upgrade.
    const result = await requestJSON(`${cartURL}/change.js`, { method: 'POST', body });
    if (!Array.isArray(result.items) || !matches(result.items.find(item => String(item.variant_id || item.id) === upgrade.variantId))) {
      throw new Error('Your pack changed, but its bonus details could not be confirmed.');
    }
    pendingUpgrade = null;
    return result;
  }

  async function mutate(action, formData, trigger, form) {
    if (busy) return;
    const previous = focusDescriptor(document.activeElement);
    const priorityAction = form?.matches('[data-br-priority]') || trigger?.matches('[data-br-priority-remove]');
    const priorityStatus = dialog.querySelector('[data-br-priority-announcement]');
    if (priorityAction && priorityStatus) priorityStatus.textContent = '';
    setError('', form);
    setBusy(true);
    formData.set('sections', sectionId);
    formData.set('sections_url', location.pathname);
    if (action === 'update' && form && /^\d+$/.test(form.dataset.upgradeVariant || '')) {
      const properties={};
      for(const [key,value] of [...formData.entries()]){
        const match=key.match(/^upgrade_properties\[(.+)\]$/);
        if(match){properties[match[1]]=value;formData.delete(key);}
      }
      properties._br_bundle=properties._br_bundle||crypto.randomUUID();
      properties['Included gift']='BEAM RIVALS Glow Bands — 4-Pack';
      pendingUpgrade={variantId:form.dataset.upgradeVariant,properties};
    }

    let confirmed = false;
    let showAfterFailure = false;
    try {
      const result = await requestJSON(`${cartURL}/${action}.js`, { method: 'POST', body: formData });
      confirmed = true;
      await reconcile(result);
      openDrawer(trigger);
    } catch (error) {
      // Shopify can add available stock even on a 422, and a lost response can follow a successful add.
      // Reconcile once without retrying a POST, so a second click cannot silently duplicate the order.
      try {
        if (!confirmed) {
          await reconcile(error.cartResponse);
          showAfterFailure = true;
        } else throw error;
      } catch (_) {
        stateUnavailable = true;
        await refreshCountOnly();
      }
      if (confirmed || showAfterFailure || dialog.open || stateUnavailable) openDrawer(trigger);
      setError(pendingUpgrade
        ? 'Your pack changed, but its bonus details could not be confirmed. Close and reopen your bag to finish the update before checkout.'
        : confirmed
        ? 'Your bag was updated. The preview could not load — use View bag to review your order.'
        : error.name === 'AbortError' || error instanceof TypeError
          ? 'We could not confirm the update. Check your bag before trying again.'
          : error.message, form);
    } finally {
      setBusy(false);
      restoreDrawerFocus(previous);
      if (priorityAction && confirmed && !stateUnavailable) {
        const offer = dialog.querySelector('.br-priority');
        const selected = offer?.dataset.prioritySelected === 'true';
        if (priorityStatus) priorityStatus.textContent = selected ? 'Skip the Line — Holiday Priority added to your order.' : 'Holiday priority removed from your order.';
        const control = offer?.querySelector('[data-br-priority-toggle]');
        if (control) { control.focus({preventScroll:true}); offer.scrollIntoView({block:'nearest',behavior:'auto'}); }
      }
      if (stateUnavailable) {
        // Stale totals and line keys must never be used for another update or checkout.
        dialog.querySelectorAll('[data-br-quantity], [data-br-upgrade] button, [data-br-extra] button, button[name="checkout"]').forEach(button => { button.disabled = true; });
      }
    }
  }

  async function showCurrentCart(trigger) {
    openDrawer(trigger);
    if (busy) return;
    const previous = focusDescriptor(document.activeElement);
    setError('');
    setBusy(true);
    try { await refreshSection(); }
    catch (error) {
      stateUnavailable = true;
      setError('Your bag preview could not load. Use View bag to review your order.');
    } finally {
      setBusy(false);
      restoreDrawerFocus(previous);
      if (stateUnavailable) dialog.querySelectorAll('[data-br-quantity], [data-br-upgrade] button, [data-br-extra] button, button[name="checkout"]').forEach(button => { button.disabled = true; });
    }
  }

  function prepareFormData(form) {
    const body = new FormData(form);
    const prepared = window.BeamBands ? window.BeamBands.prepare(form, body) : body;
    return window.BeamExtras ? window.BeamExtras.prepare(form, prepared) : prepared;
  }

  document.addEventListener('submit', async event => {
    const form=event.target;
    if(!(form instanceof HTMLFormElement)||!form.matches('[data-br-cart-page], [data-br-checkout]'))return;
    event.preventDefault();if(busy)return;
    const checkout=event.submitter?.name==='checkout';
    const body=new FormData(form);setBusy(true);setError('');
    try{
      let cart;
      if(form.matches('[data-br-cart-page]'))cart=await requestJSON(`${cartURL}/update.js`,{method:'POST',body});
      else cart=await requestJSON(`${cartURL}.js`,{cache:'no-store'});
      cart = await syncPriority(cart);
      if(window.BeamBands) await window.BeamBands.sync(cart,requestJSON,cartURL);
      if(checkout)location.href=`${root.replace(/\/?$/,'/')}checkout`;
      else location.reload();
    }catch(error){
      stateUnavailable=true;openDrawer(event.submitter);setError(error.message);
    }finally{setBusy(false)}
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-br-add], [data-br-extra], [data-br-upgrade]')) return;
    event.preventDefault();
    if (busy || (dialog.contains(form) && stateUnavailable)) return;
    const action = form.matches('[data-br-upgrade], [data-br-priority]') ? 'update' : 'add';
    // Capture controls before disabling them, including the selected variant.
    try {
      mutate(action, prepareFormData(form), event.submitter || document.activeElement, form);
    } catch (error) { setError(error.message, form); }
  });

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const preview = target.closest('[data-br-preview-cart]');
    if (preview && window.Shopify && window.Shopify.designMode) {
      event.preventDefault();
      openDrawer(preview);
      return;
    }
    const open = target.closest('[data-br-cart-open]');
    if (open) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      showCurrentCart(open);
      return;
    }
    if (!dialog.contains(target)) return;
    if (target.closest('[data-br-cart-close]')) { event.preventDefault(); closeDrawer(); return; }
    const quantity = target.closest('[data-br-quantity]');
    if (quantity) {
      event.preventDefault();
      if (busy || stateUnavailable || quantity.disabled) return;
      const value = Number(quantity.dataset.quantity);
      if (!quantity.dataset.key || !Number.isInteger(value) || value < 0) return;
      const body = new FormData();
      body.set('id', quantity.dataset.key);
      body.set('quantity', String(value));
      mutate('change', body, quantity);
    }
  });

  dialog.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
  let backdropPress = false;
  dialog.addEventListener('pointerdown', event => {
    const rect = dialog.getBoundingClientRect();
    backdropPress = event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
  });
  dialog.addEventListener('click', event => {
    if (backdropPress && event.target === dialog) closeDrawer();
    backdropPress = false;
  });
  dialog.addEventListener('close', () => {
    clearTimeout(closeTimer);
    document.body.classList.remove('br-cart-open');
    dialog.classList.remove('is-closing');
    const target = opener;
    opener = null;
    if(document.querySelector('[data-br-cart-page]')){location.reload();return;}
    if (target && target.isConnected && !target.disabled) target.focus({ preventScroll: true });
  });

  if (content()) updateCount(content().dataset.itemCount);
})();
